import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getInfo, getNotificationSettings, updateNotificationSettings, updateLocation, updateCompany } from '../services/api';
import { track } from '../services/analytics';
import BrowserAlertsSetting from './BrowserAlertsSetting';
import { formatPhoneNumber } from '../utils/displayFormatters';

// IANA zones staff can pick from — anchors what "today" means for this location
const TIMEZONE_OPTIONS = [
  { value: 'America/New_York',    label: 'Eastern — New York'    },
  { value: 'America/Toronto',     label: 'Eastern — Toronto'     },
  { value: 'America/Chicago',     label: 'Central — Chicago'     },
  { value: 'America/Denver',      label: 'Mountain — Denver'     },
  { value: 'America/Phoenix',     label: 'Mountain — Phoenix (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
  { value: 'America/Anchorage',   label: 'Alaska — Anchorage'    },
  { value: 'Pacific/Honolulu',    label: 'Hawaii — Honolulu'     },
  { value: 'UTC',                 label: 'UTC'                   },
];

// One booking-link input with its own Save button. onSave(value) resolves when saved
// and throws on a refusal; the server's message is shown under the input.
const BookingLinkField = ({ value, placeholder, canEdit, onSave, dataAttr }) => {
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // 'saved' | message string | null

  useEffect(() => setDraft(value || ''), [value]);

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await onSave(draft.trim());
      setStatus('saved');
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      const data = err.response?.data;
      setStatus((data?.booking_url && data.booking_url[0]) || data?.detail || "Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <input
          type="url"
          data-attr={dataAttr}
          aria-label="Online booking link"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={placeholder}
          disabled={!canEdit || saving}
          className="w-full sm:w-96 border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:opacity-50"
        />
        {canEdit && (
          <button
            type="button"
            onClick={save}
            disabled={saving || draft.trim() === (value || '')}
            className="rounded-md bg-teal-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        {status === 'saved' && <span className="text-sm text-green-700 font-medium">Saved</span>}
      </div>
      {status && status !== 'saved' && (
        <p role="alert" className="mt-1 text-sm text-red-600">{status}</p>
      )}
    </div>
  );
};

const Info = ({}) => {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  // Notification settings state
  const [notifSettings, setNotifSettings] = useState(null);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifStatus, setNotifStatus] = useState(null); // 'saved' | 'error' | null

  // Per-location timezone state
  const [tzSavingId, setTzSavingId] = useState(null);
  const [tzStatus, setTzStatus] = useState({}); // { [locationId]: 'saved' | 'error' }

  // Per-location missed-call text state
  const [textbackSavingId, setTextbackSavingId] = useState(null);
  const [textbackStatus, setTextbackStatus] = useState({}); // { [locationId]: 'saved' | 'error' }

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [infoRes, notifRes] = await Promise.all([getInfo(), getNotificationSettings()]);
        setInfo(infoRes);
        setNotifSettings(notifRes);
      } catch (err) {
        console.error('Error fetching info:', err);
        setError(err.response?.data || 'An error occurred');
      }
    };
    fetchAll();
  }, []);

  // Dismiss "saved" toast after 3 s
  useEffect(() => {
    if (notifStatus === 'saved') {
      const t = setTimeout(() => setNotifStatus(null), 3000);
      return () => clearTimeout(t);
    }
  }, [notifStatus]);

  const handleNotifChange = useCallback((field, value) => {
    setNotifSettings(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleLocation = useCallback((locationId) => {
    setNotifSettings(prev => {
      const current = prev.notification_locations || [];
      const next = current.includes(locationId)
        ? current.filter(id => id !== locationId)
        : [...current, locationId];
      return { ...prev, notification_locations: next };
    });
  }, []);

  const handleTimezoneChange = useCallback(async (locationId, value) => {
    setTzSavingId(locationId);
    setTzStatus(prev => ({ ...prev, [locationId]: null }));
    try {
      const updated = await updateLocation(locationId, { timezone: value });
      setInfo(prev => ({
        ...prev,
        locations: prev.locations.map(loc =>
          loc.id === locationId
            ? { ...loc, timezone: updated.timezone, effective_timezone: updated.effective_timezone }
            : loc
        ),
      }));
      setTzStatus(prev => ({ ...prev, [locationId]: 'saved' }));
      setTimeout(() => setTzStatus(prev => ({ ...prev, [locationId]: null })), 3000);
    } catch (err) {
      console.error('Error saving location timezone:', err);
      setTzStatus(prev => ({ ...prev, [locationId]: 'error' }));
    } finally {
      setTzSavingId(null);
    }
  }, []);

  const handleCompanyBookingUrl = useCallback(async (value) => {
    const updated = await updateCompany(info.company.id, { booking_url: value });
    track('booking_link_saved', { level: 'company', has_link: !!updated.booking_url });
    setInfo(prev => ({
      ...prev,
      company: { ...prev.company, booking_url: updated.booking_url },
      locations: prev.locations.map(loc =>
        loc.booking_url ? loc : { ...loc, effective_booking_url: updated.booking_url }
      ),
    }));
  }, [info]);

  const handleLocationBookingUrl = useCallback(async (locationId, value) => {
    const updated = await updateLocation(locationId, { booking_url: value });
    track('booking_link_saved', { level: 'location', has_link: !!updated.booking_url });
    setInfo(prev => ({
      ...prev,
      locations: prev.locations.map(loc =>
        loc.id === locationId
          ? { ...loc, booking_url: updated.booking_url, effective_booking_url: updated.effective_booking_url }
          : loc
      ),
    }));
  }, []);

  const handleTextbackChange = useCallback(async (locationId, enabled) => {
    setTextbackSavingId(locationId);
    setTextbackStatus(prev => ({ ...prev, [locationId]: null }));
    try {
      const updated = await updateLocation(locationId, { textback_enabled: enabled });
      setInfo(prev => ({
        ...prev,
        locations: prev.locations.map(loc =>
          loc.id === locationId ? { ...loc, textback_enabled: updated.textback_enabled } : loc
        ),
      }));
      track('missed_call_text_toggled', { location_id: locationId, enabled: updated.textback_enabled });
      setTextbackStatus(prev => ({ ...prev, [locationId]: 'saved' }));
      setTimeout(() => setTextbackStatus(prev => ({ ...prev, [locationId]: null })), 3000);
    } catch (err) {
      console.error('Error saving missed-call text setting:', err);
      setTextbackStatus(prev => ({ ...prev, [locationId]: 'error' }));
    } finally {
      setTextbackSavingId(null);
    }
  }, []);

  const handleNotifSave = async () => {
    setNotifSaving(true);
    setNotifStatus(null);
    try {
      const updated = await updateNotificationSettings({
        notify_via_email: notifSettings.notify_via_email,
        notify_via_sms: notifSettings.notify_via_sms,
        notification_email: notifSettings.notification_email || null,
        notification_phone: notifSettings.notification_phone || null,
        notify_on_phone_leads: notifSettings.notify_on_phone_leads,
        notify_on_webform_leads: notifSettings.notify_on_webform_leads,
        notify_on_chatbot_leads: notifSettings.notify_on_chatbot_leads,
        notify_on_prospect_leads: notifSettings.notify_on_prospect_leads,
        notify_on_existing_patient_leads: notifSettings.notify_on_existing_patient_leads,
        notification_locations: notifSettings.notification_locations,
      });
      setNotifSettings(updated);
      track('notification_settings_saved', {
        email_enabled: updated.notify_via_email,
        sms_enabled: updated.notify_via_sms,
        location_filter_count: (updated.notification_locations || []).length,
        notify_on_prospect_leads: updated.notify_on_prospect_leads,
        notify_on_existing_patient_leads: updated.notify_on_existing_patient_leads,
      });
      setNotifStatus('saved');
    } catch (err) {
      console.error('Error saving notification settings:', err);
      setNotifStatus('error');
    } finally {
      setNotifSaving(false);
    }
  };

  if (error) {
    return <div className="text-red-500">Error: {JSON.stringify(error)}</div>;
  }

  if (!info) {
    return <div>Loading...</div>;
  }

  const companyTimezone =
    (typeof info.company === 'object' && info.company?.timezone) || 'UTC';

return (
  <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-background">
    <div className="flex justify-between items-center mb-8">
      <h1 className="text-2xl font-bold text-gray-800">
        {info.company?.name || info.company}
      </h1>
    </div>

    <div className="bg-white shadow-sm rounded-lg hover:border hover:border-teal-200 transition duration-300 overflow-hidden mb-8">
      <div className="px-4 py-5 sm:px-6 bg-accent-teal-light-bg border-b border-border">
        <h2 className="text-lg font-semibold text-gray-800">
          Online booking link
        </h2>
      </div>
      <div className="px-4 py-5 sm:p-6">
        <BookingLinkField
          dataAttr="company-booking-link"
          value={info.company?.booking_url}
          placeholder="https://yourpractice.com/book"
          canEdit={!!info.is_company_admin}
          onSave={handleCompanyBookingUrl}
        />
        <p className="mt-1 text-xs text-gray-500">
          Used by every location that has no link of its own. Insert it in any text with{" "}
          {"{{booking_url}}"}.
          {!info.is_company_admin && " Ask your company admin to change this."}
        </p>
      </div>
    </div>

    <div className="bg-white shadow-sm rounded-lg hover:border hover:border-teal-200 transition duration-300 overflow-hidden">
      <div className="px-4 py-5 sm:px-6 bg-accent-teal-light-bg border-b border-border">
        <h2 className="text-lg font-semibold text-gray-800">
          User Information
        </h2>
      </div>
      <div className="px-4 py-5 sm:p-6">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-600">Username</dt>
            <dd className="mt-1 text-base font-medium text-gray-900">
              {info.user_name}
            </dd>
            <Link
              to="/change-password"
              className="mt-2 inline-block text-sm font-medium text-primary hover:text-teal-800 transition duration-200 hover:underline focus:outline-none focus:ring-2 focus:ring-text-primary focus:ring-offset-2"
            >
              Change Password
            </Link>
          </div>
          {(info.first_name || info.last_name) && (
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-600">Name</dt>
              <dd className="mt-1 text-base text-gray-900">
                {`${info.first_name} ${info.last_name}`.trim()}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>

    <BrowserAlertsSetting />

    {/* Lead Notification Settings */}
    {notifSettings && (
      <div className="mt-10 bg-white shadow-sm rounded-lg hover:border hover:border-teal-200 transition duration-300 overflow-hidden">
        <div className="px-4 py-5 sm:px-6 bg-accent-teal-light-bg border-b border-border">
          <h2 className="text-lg font-semibold text-gray-800">Which leads alert you</h2>
          <p className="mt-1 text-sm text-gray-600">
            Choose how and which new-lead alerts you want to receive by email, text or in your browser
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6 space-y-6">

          {/* ── Channels ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Notification Channels</h3>
            <div className="space-y-4">

              {/* Email toggle + override */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifSettings.notify_via_email}
                    onChange={e => handleNotifChange('notify_via_email', e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-700 focus:ring-teal-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Email</span>
                    <p className="text-xs text-gray-500">Receive alerts via email</p>
                  </div>
                </label>
                {notifSettings.notify_via_email && (
                  <div className="ml-7 mt-2">
                    <label className="text-xs font-medium text-gray-600">Notification email (optional)</label>
                    <input
                      type="email"
                      value={notifSettings.notification_email || ''}
                      onChange={e => handleNotifChange('notification_email', e.target.value)}
                      placeholder="Defaults to your location's email"
                      className="mt-1 w-full border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 placeholder-gray-400"
                    />
                  </div>
                )}
              </div>

              {/* SMS toggle + phone */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifSettings.notify_via_sms}
                    onChange={e => handleNotifChange('notify_via_sms', e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-700 focus:ring-teal-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">SMS</span>
                    <p className="text-xs text-gray-500">Receive alerts via text message</p>
                  </div>
                </label>
                {notifSettings.notify_via_sms && (
                  <div className="ml-7 mt-2">
                    <label className="text-xs font-medium text-gray-600">Phone number <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      value={notifSettings.notification_phone || ''}
                      onChange={e => handleNotifChange('notification_phone', e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="mt-1 w-full border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 placeholder-gray-400"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Lead Types ── */}
          <div className="border-t border-border pt-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Lead Types</h3>
            <p className="text-xs text-gray-500 mb-3">Select which types of new leads should trigger a notification</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {[
                { key: 'notify_on_phone_leads',   label: 'Phone Calls' },
                { key: 'notify_on_webform_leads', label: 'Webform'     },
                { key: 'notify_on_chatbot_leads', label: 'Chatbot'     },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifSettings[key]}
                    onChange={e => handleNotifChange(key, e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-teal-700 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-900">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* ── Lead Categories ── */}
          <div className="border-t border-border pt-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Lead Categories</h3>
            <p className="text-xs text-gray-500 mb-3">Select which categories of new leads should trigger a notification</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {[
                { key: 'notify_on_prospect_leads',         label: 'Prospects'         },
                { key: 'notify_on_existing_patient_leads', label: 'Existing Patients' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifSettings[key]}
                    onChange={e => handleNotifChange(key, e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-teal-700 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-900">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* ── Location Filter (only when >1 location) ── */}
          {notifSettings.available_locations && notifSettings.available_locations.length > 1 && (
            <div className="border-t border-border pt-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Locations</h3>
              <p className="text-xs text-gray-500 mb-3">
                Pick specific locations to be notified about. Leave all unchecked to receive alerts from every location you're assigned to.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {notifSettings.available_locations.map(loc => (
                  <label key={loc.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(notifSettings.notification_locations || []).includes(loc.id)}
                      onChange={() => toggleLocation(loc.id)}
                      className="h-4 w-4 rounded border-gray-300 text-teal-700 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-900">{loc.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ── Save ── */}
          <div className="border-t border-border pt-5 flex items-center gap-4">
            <button
              data-attr="notif-settings-save"
              onClick={handleNotifSave}
              disabled={notifSaving}
              className="bg-teal-700 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-teal-800 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {notifSaving ? 'Saving…' : 'Save'}
            </button>
            {notifStatus === 'saved' && (
              <span className="text-sm text-green-700 font-medium">Settings saved</span>
            )}
            {notifStatus === 'error' && (
              <span className="text-sm text-red-600 font-medium">
                {notifSettings.notify_via_sms && !notifSettings.notification_phone
                  ? 'A phone number is required when SMS is enabled.'
                  : 'Something went wrong — please try again.'}
              </span>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Company Information Section */}
    {info.company && typeof info.company === "object" && (
      <div className="mt-10 bg-white shadow-sm rounded-lg hover:border hover:border-teal-200 transition duration-300 overflow-hidden">
        <div className="px-4 py-5 sm:px-6 bg-accent-teal-light-bg border-b border-border">
          <h2 className="text-lg font-semibold text-gray-800">
            Company Email Configuration
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Default email settings applied to all locations (can be overridden
            per location)
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-600">
                Email Domain
              </dt>
              <dd className="mt-1 text-base text-gray-900">
                {info.company.email_domain || (
                  <span className="text-gray-400 italic">Not configured</span>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-600">From Name</dt>
              <dd className="mt-1 text-base text-gray-900">
                {info.company.email_from_name || (
                  <span className="text-gray-400 italic">Not configured</span>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-600">
                From Address
              </dt>
              <dd className="mt-1 text-base text-gray-900">
                {info.company.email_from_address || (
                  <span className="text-gray-400 italic">Not configured</span>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-600">
                Domain Verified
              </dt>
              <dd className="mt-1 text-base">
                {info.company.domain_verified ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    ✗ Not Verified
                  </span>
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    )}

    <div className="mt-10 bg-white shadow-sm rounded-lg hover:border hover:border-teal-200 transition duration-300 overflow-hidden">
      <div className="px-4 py-5 sm:px-6 bg-accent-teal-light-bg border-b border-border">
        <h2 className="text-lg font-semibold text-gray-800">Locations</h2>
      </div>
      <div className="border-t border-border">
        {info.locations &&
          info.locations.map((location, index) => (
            <div
              key={location.form_id}
              className={`px-4 py-5 sm:p-6 ${
                index !== 0 ? "border-t border-border" : ""
              }`}
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {location.name}
              </h3>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-600">Address</dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {location.address}
                  </dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-600">Phone</dt>
                  <dd className="mt-1 text-base text-primary">
                    {formatPhoneNumber(location.phone)}
                  </dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-600">Email</dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {location.email}
                  </dd>
                </div>
                {location.notification_email && (
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-600">
                      Notification Email
                    </dt>
                    <dd className="mt-1 text-base text-gray-900">
                      {location.notification_email}
                    </dd>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-600">Form ID</dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {location.form_id}
                  </dd>
                </div>

                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-600">Timezone</dt>
                  <dd className="mt-1 flex items-center gap-3">
                    <select
                      data-attr="location-timezone"
                      value={location.timezone || ''}
                      onChange={e => handleTimezoneChange(location.id, e.target.value)}
                      disabled={tzSavingId === location.id}
                      className="w-full sm:w-72 border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:opacity-50"
                    >
                      <option value="">
                        Company default ({companyTimezone})
                      </option>
                      {TIMEZONE_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                      {location.timezone &&
                        !TIMEZONE_OPTIONS.some(o => o.value === location.timezone) && (
                          <option value={location.timezone}>{location.timezone}</option>
                        )}
                    </select>
                    {tzSavingId === location.id && (
                      <span className="text-sm text-gray-500">Saving…</span>
                    )}
                    {tzStatus[location.id] === 'saved' && (
                      <span className="text-sm text-green-700 font-medium">Saved</span>
                    )}
                    {tzStatus[location.id] === 'error' && (
                      <span className="text-sm text-red-600 font-medium">
                        Couldn't save — please try again.
                      </span>
                    )}
                  </dd>
                  <p className="mt-1 text-xs text-gray-500">
                    Anchors what "today" means for this location's dashboards, date
                    filters and reminder dates. Currently{" "}
                    <span className="font-medium text-gray-700">
                      {location.effective_timezone}
                    </span>
                    .
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-600">Online booking link</dt>
                  <dd className="mt-1">
                    <BookingLinkField
                      dataAttr="location-booking-link"
                      value={location.booking_url}
                      placeholder={info.company?.booking_url || "Uses the company link"}
                      canEdit={!!info.is_company_admin}
                      onSave={value => handleLocationBookingUrl(location.id, value)}
                    />
                  </dd>
                  <p className="mt-1 text-xs text-gray-500">
                    {location.booking_url
                      ? "This location uses its own link. Clear it to use the company link."
                      : info.company?.booking_url
                        ? "This location uses the company link."
                        : "No link set. Texts that use {{booking_url}} will leave it blank."}
                  </p>
                </div>

                {/* Missed-call text: self-serve for company admins, off by default */}
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-600">Text missed callers</dt>
                  <dd className="mt-1 flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-900">
                      <input
                        type="checkbox"
                        data-attr="location-missed-call-text"
                        checked={!!location.textback_enabled}
                        onChange={e => handleTextbackChange(location.id, e.target.checked)}
                        disabled={
                          textbackSavingId === location.id ||
                          !location.messaging_enabled ||
                          !info.is_company_admin
                        }
                        className="h-4 w-4 rounded border-border text-teal-700 focus:ring-teal-500 disabled:opacity-50"
                      />
                      {location.textback_enabled ? "On" : "Off"}
                    </label>
                    {textbackSavingId === location.id && (
                      <span className="text-sm text-gray-500">Saving…</span>
                    )}
                    {textbackStatus[location.id] === 'saved' && (
                      <span className="text-sm text-green-700 font-medium">Saved</span>
                    )}
                    {textbackStatus[location.id] === 'error' && (
                      <span className="text-sm text-red-600 font-medium">
                        Couldn't save — please try again.
                      </span>
                    )}
                  </dd>
                  <p className="mt-1 text-xs text-gray-500">
                    When a call to this location is missed, Dentara texts the caller.
                    {!location.messaging_enabled
                      ? " Texting is not set up for this location. Ask Dentara to set it up."
                      : !info.is_company_admin
                        ? " Ask your company admin to change this."
                        : ""}
                  </p>
                </div>

                {/* Email Configuration Section */}
                <div className="sm:col-span-2 border-t border-border pt-6 mt-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                    Email Configuration
                    <span
                      className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        location.email_configured
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {location.email_configured
                        ? "✓ Configured"
                        : "⚠ Not Configured"}
                    </span>
                  </h4>

                  {location.effective_email_settings && (
                    <div className="bg-background rounded-lg p-4">
                      <div className="mb-3">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Configuration Source:{" "}
                          {location.effective_email_settings.source ===
                          "company"
                            ? "Company Default"
                            : "Location Override"}
                        </span>
                        {location.effective_email_settings.source ===
                          "company" && (
                          <span className="ml-2 text-xs text-blue-600">
                            (Using company defaults)
                          </span>
                        )}
                        {location.effective_email_settings.source ===
                          "location" && (
                          <span className="ml-2 text-xs text-green-600">
                            (Location-specific settings)
                          </span>
                        )}
                      </div>

                      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 text-sm">
                        <div>
                          <dt className="font-medium text-gray-600">
                            Effective Domain
                          </dt>
                          <dd className="mt-1 text-gray-900">
                            {location.effective_email_settings.email_domain || (
                              <span className="text-gray-400 italic">None</span>
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-600">
                            Effective From Name
                          </dt>
                          <dd className="mt-1 text-gray-900">
                            {location.effective_email_settings
                              .email_from_name || (
                              <span className="text-gray-400 italic">None</span>
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-600">
                            Effective From Address
                          </dt>
                          <dd className="mt-1 text-gray-900">
                            {location.effective_email_settings
                              .email_from_address || (
                              <span className="text-gray-400 italic">None</span>
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-600">
                            Domain Verified
                          </dt>
                          <dd className="mt-1">
                            {location.effective_email_settings
                              .mailgun_domain_verified ? (
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                ✓ Verified
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                ✗ Not Verified
                              </span>
                            )}
                          </dd>
                        </div>
                      </dl>

                      {/* Location Override Details */}
                      {location.email_domain && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">
                            Location Override Settings:
                          </h5>
                          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 text-sm">
                            <div>
                              <dt className="font-medium text-gray-600">
                                Override Domain
                              </dt>
                              <dd className="text-gray-900">
                                {location.email_domain}
                              </dd>
                            </div>
                            <div>
                              <dt className="font-medium text-gray-600">
                                Override From Name
                              </dt>
                              <dd className="text-gray-900">
                                {location.email_from_name || (
                                  <span className="text-gray-400 italic">
                                    None
                                  </span>
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt className="font-medium text-gray-600">
                                Override From Address
                              </dt>
                              <dd className="text-gray-900">
                                {location.email_from_address || (
                                  <span className="text-gray-400 italic">
                                    None
                                  </span>
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt className="font-medium text-gray-600">
                                Override Verified
                              </dt>
                              <dd>
                                {location.domain_verified ? (
                                  <span className="text-green-600 text-xs">
                                    ✓ Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600 text-xs">
                                    ✗ No
                                  </span>
                                )}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </dl>
            </div>
          ))}
      </div>
    </div>
  </div>
);

};

export default Info;